import {Component, OnInit} from '@angular/core';
import {combineLatest, forkJoin, Observable, of} from "rxjs";
import {map, switchMap, tap} from "rxjs/operators";
import {ActivatedRoute} from "@angular/router";
import {StudentV3RestService} from "../../services/rest/v3/student-rest.service";
import {V3Student} from "../../model/v3/schools";
import {CspaRestServiceV3} from "../../services/rest/v3/cspa-rest.service";
import {V3ExamSession, V3ExamSessionPart, V3ExerciseSet} from "../../model/v3/cspa";

@Component({
    selector: 'app-manager-student-exam-results',
    templateUrl: './manager-student-exam-results.component.html',
    styleUrls: ['./manager-student-exam-results.component.scss'],
    standalone: false
})
export class ManagerStudentExamResultsComponent implements OnInit {

  exams: V3ExamSession[]
  student: V3Student;
  exerciseSets: Map<string, V3ExerciseSet> = null;
  exerciseSetExams: Map<V3ExerciseSet, V3ExamSession[]> = new Map<V3ExerciseSet, V3ExamSession[]>();
  examPartsBySessionUuid: Map<string, V3ExamSessionPart[]> = new Map<string, V3ExamSessionPart[]>();
  exerciseSetEntries: [V3ExerciseSet, V3ExamSession[]][] = []
  private readonly emptyParts: V3ExamSessionPart[] = []

  constructor(private route: ActivatedRoute,
              private cspaService: CspaRestServiceV3,
              private studentV3Rest: StudentV3RestService
  ) { }

  ngOnInit(): void {
    combineLatest(
      [this.route.parent.parent.paramMap,
        this.route.parent.paramMap]
    ).pipe(
      map(([parentParams, params]) => [Number(parentParams.get('schoolId')), Number(params.get('studentId'))]),
      switchMap(([schoolId, studentId]) =>
        this.studentV3Rest.getStudent(schoolId, studentId)
      ),
      tap( student => this.student = student ),
      switchMap( _ => this.loadExerciseSets()),
      switchMap(_ => this.cspaService.findExamSessions(this.student.person.id, "")),
      switchMap(exams => this.loadExamParts(exams).pipe(
        map(partsByUuid => ({ exams, partsByUuid }))
      ))
    ).subscribe(({ exams, partsByUuid }) =>{
        this.exams = exams.sort((a, b) => a.path.localeCompare(b.path))
        this.examPartsBySessionUuid = partsByUuid
        this.setExerciseSetExams()
      })
  }

  loadExerciseSets(): Observable<any> {
    if (this.exerciseSets && this.exerciseSets.size > 0) return of(this.exerciseSets)
    return this.cspaService.listExerciseSets().pipe(
      map( sets =>
        sets.reduce((res,current)=> res.set(current.path, current)
          ,new Map<string, V3ExerciseSet>())),
      tap( setsMap => this.exerciseSets = setsMap)
    )
  }

  loadExamParts(exams: V3ExamSession[]): Observable<Map<string, V3ExamSessionPart[]>> {
    if (!exams || exams.length === 0) {
      return of(new Map<string, V3ExamSessionPart[]>());
    }
    return forkJoin(
      exams.map(exam =>
        this.cspaService.findExamSessionsParts(this.student.person.id, exam.uuid).pipe(
          map(parts => [exam.uuid, parts] as [string, V3ExamSessionPart[]])
        )
      )
    ).pipe(
      map(entries => new Map<string, V3ExamSessionPart[]>(entries))
    );
  }

  getExamParts(exam: V3ExamSession) {
    return this.examPartsBySessionUuid.get(exam.uuid) || this.emptyParts
  }

  setExerciseSetExams() {
    this.exerciseSetExams.clear()
    for (let set of this.exerciseSets.values()) {
      this.exerciseSetExams.set(
        set,
        this.exams.filter(e => e.path.split("_")[0] == set.path)
          .sort((a, b) =>
            a.path.split("_")[3].localeCompare(b.path.split("_")[3])
          )
          .sort((a, b) =>
            +a.path.split("_")[1] - +b.path.split("_")[1]
          )
      )
    }
    this.exerciseSetEntries = Array.from(this.exerciseSetExams.entries())
      .filter(([_, exams]) => exams.length > 0)
  }

  getExamScore(exam: V3ExamSession) {
    const parts = this.examPartsBySessionUuid.get(exam.uuid) || [];
    return parts.reduce((sum, p) => sum + (p.score * p.weight), 0)
  }

  trackByExerciseSet(_: number, entry: [V3ExerciseSet, V3ExamSession[]]): string {
    return entry[0].path
  }

  trackByExamSession(_: number, exam: V3ExamSession): string {
    return exam.uuid
  }

  trackByExamPart(_: number, examPart: V3ExamSessionPart): string {
    return `${examPart.path}:${examPart.num}`
  }
}
